import React, { useCallback, useContext, useState } from 'react';
import { useStore, Position, getSmoothStepPath, EdgeLabelRenderer } from 'reactflow';
import { Tooltip, IconButton } from '@mui/material';
import LoopIcon from '@mui/icons-material/Loop';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { CanvasContext } from '../../WorkflowCanvas';
import { createSaveEvent } from '../../../../../utils/createSaveEvent';
import { saveCanvasEvent } from '../../../../../utils/canvasApi';
import useProjectStore from '../../../../ProjectCanvas/useProjectStore';
import { getElements } from '../../../../../utils/CanvasUtil';
import useCanvasStore from '../../useCanvasStore';

const NODE_WIDTH = 250;
const NODE_HEIGHT = 80;
const CONNECTOR_OFFSET = 8;
const PATH_HEIGHT_OFFSET = 120;
const BOX_PADDING = 50;

const LoopLink = ({ id, source, target, data, selected }) => {
    const nodes = useCanvasStore(s => s.nodes);
    const edges = useCanvasStore(s => s.edges);
    const [isHovered, setIsHovered] = useState(false);
    
    const { modeInfo, canvasInstance, savingInfo, updated } = useContext(CanvasContext);
    const [mode] = modeInfo || [null];
    const [reactFlowInstance] = canvasInstance || [null];
    const [saving, setSaving] = savingInfo || [false, () => {}];
    const [lastModified, setLastModified] = updated || [null, () => {}];

    const sourceNode = useStore(useCallback(store => store.nodeInternals.get(source), [source]));
    const targetNode = useStore(useCallback(store => store.nodeInternals.get(target), [target]));

    if (!sourceNode || !targetNode) return null;

    const maxIterations = data?.maxIterations || 10;
    const exitCondition = data?.exitCondition || null;
    const currentIteration = data?.currentIteration || 0;
    const isProgressView = mode === 'PROGRESSVIEW';

    // Generate chip label based on condition
    const getChipLabel = () => {
      if (isProgressView) {
        return `${currentIteration}/${maxIterations}`;
      }
      
      const conditionsList = exitCondition?.rules || exitCondition?.conditions;
      if (exitCondition?.enabled && conditionsList?.length > 0) {
        // Show shorthand of first condition
        const firstCondition = conditionsList[0];
        const key = firstCondition.field || firstCondition.key || '';
        const shortKey = key.length > 15 ? key.substring(0, 12) + '...' : key;
        return `Loop ${maxIterations}x | ${shortKey}`;
      }
      
      return `Loop ${maxIterations}x`;
    };

    // Calculate how many nested loops are inside this one to add extra padding
    const currentLoopLeft = Math.min(sourceNode.position.x, targetNode.position.x);
    const currentLoopRight = Math.max(sourceNode.position.x, targetNode.position.x) + NODE_WIDTH;
    
    // Count loops that are contained within this loop's x-range
    const nestedLoopCount = edges.filter(edge => {
      if (edge.type !== 'loopLink' || edge.id === id) return false;
      
      const otherSourceNode = nodes.find(n => n.id === edge.source);
      const otherTargetNode = nodes.find(n => n.id === edge.target);
      if (!otherSourceNode || !otherTargetNode) return false;
      
      const otherLeft = Math.min(otherSourceNode.position.x, otherTargetNode.position.x);
      const otherRight = Math.max(otherSourceNode.position.x, otherTargetNode.position.x) + NODE_WIDTH;
      
      // Check if the other loop is contained within this one
      return otherLeft >= currentLoopLeft && otherRight <= currentLoopRight;
    }).length;
    
    // Add 20px extra padding for each nested loop
    const dynamicPadding = BOX_PADDING + (nestedLoopCount * 20);
    // Also increase path height offset so the loop edge stays above the box border
    const dynamicPathOffset = PATH_HEIGHT_OFFSET + (nestedLoopCount * 20);

    // Calculate path
    const sourceX = sourceNode.position.x + NODE_WIDTH / 2 + CONNECTOR_OFFSET;
    const targetX = targetNode.position.x + NODE_WIDTH / 2 - CONNECTOR_OFFSET;

    const edgePath = getSmoothStepPath({
      sourceX,
      sourceY: sourceNode.position.y,
      sourcePosition: Position.Top,
      targetPosition: Position.Top,
      targetX,
      targetY: targetNode.position.y,
      offset: dynamicPathOffset,
    });

    if (!edgePath?.length) return null;

    const centerX = (sourceX + targetX) / 2;
    const minY = Math.min(sourceNode.position.y, targetNode.position.y);
    const centerY = minY - dynamicPathOffset;

    const markerId = `arrowhead-loop-${id}`;

    // Calculate bounding box for loop background
    const boxLeft = currentLoopLeft - dynamicPadding;
    const boxRight = currentLoopRight + dynamicPadding;
    const boxTop = minY - dynamicPathOffset - dynamicPadding;
    const boxBottom = Math.max(sourceNode.position.y, targetNode.position.y) + NODE_HEIGHT + dynamicPadding;
    const boxWidth = boxRight - boxLeft;
    const boxHeight = boxBottom - boxTop;

    // Handle delete
    const handleDelete = async (e) => {
      e.stopPropagation();
      if (saving || mode !== 'DEFAULT') return;

      setSaving(true);

      // Remove the link from edges
      const result = edges.filter(edge => edge.id !== id);
      useCanvasStore.getState().setEdges(result);

      if (!reactFlowInstance) {
        setSaving(false);
        return;
      }

      const rfiObj = reactFlowInstance.toObject();
      const viewport = {
        x: rfiObj.viewport.x.toFixed(5),
        y: rfiObj.viewport.y.toFixed(5),
        zoom: rfiObj.viewport.zoom.toFixed(5),
      };

      const eventData = {
        sourceComponentId: source,
        targetComponentId: target,
        noteId: id,
      };

      const saveEvent = createSaveEvent(
        'DELETE_LINK',
        useProjectStore.getState().project_id,
        viewport,
        getElements(reactFlowInstance),
        eventData
      );

      try {
        const response = await saveCanvasEvent(useProjectStore.getState().project_id, saveEvent);
        if (response?.data?.updated) {
          setLastModified(response.data.lastModified);
        }
      } catch (error) {
        console.error('Error deleting loop link:', error);
      } finally {
        setSaving(false);
      }
    };

    // Handle edit click
    const handleEdit = (e) => {
      e.stopPropagation();
      if (mode !== 'DEFAULT') return;
      
      // Dispatch custom event to open edit dialog
      window.dispatchEvent(new CustomEvent('editLoopEdge', {
        detail: { edgeId: id, source, target, maxIterations, exitCondition }
      }));
    };

    const showControls = (selected || isHovered) && mode === 'DEFAULT' && !isProgressView;

    return (
      <>
        <defs>
          <marker
            id={markerId}
            viewBox="0 0 10 10"
            refX="6"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto"
          >
            <path d="M 0 0 L 7 5 L 0 10 z" fill="#000000" />
          </marker>
        </defs>

        {/* Background box encompassing loop source and target components */}
        <rect
          x={boxLeft}
          y={boxTop}
          width={boxWidth}
          height={boxHeight}
          rx={8}
          ry={8}
          fill="none"
          stroke={isHovered || selected ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.25)'}
          strokeWidth={isHovered || selected ? 3 : 2}
          strokeDasharray="6,4"
          style={{ 
            pointerEvents: 'none',
            transition: 'stroke 0.2s ease, stroke-width 0.2s ease',
          }}
        />

        {/* Invisible wider path for easier clicking */}
        <path
          d={edgePath[0]}
          fill="none"
          stroke="transparent"
          strokeWidth={20}
          style={{ cursor: 'pointer' }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={handleEdit}
        />

        {/* Visible path - solid black to match exit edges */}
        <path
          id={id}
          className="react-flow__edge-path"
          d={edgePath[0]}
          markerEnd={`url(#${markerId})`}
          fill="none"
          style={{
            strokeWidth: isHovered || selected ? 4 : 3,
            stroke: '#000000',
            pointerEvents: 'none',
            filter: isHovered || selected ? 'drop-shadow(0 0 3px rgba(0, 0, 0, 0.4))' : 'none',
            transition: 'stroke-width 0.2s ease, filter 0.2s ease',
          }}
        />

        {/* Edge label with loop info and controls */}
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${centerX}px, ${centerY}px)`,
              pointerEvents: 'all',
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {/* Main loop badge */}
            <Tooltip 
              title={isProgressView 
                ? `Loop: ${currentIteration} of ${maxIterations} iterations completed` 
                : exitCondition?.enabled 
                  ? `Loop: exit when condition met, or after ${maxIterations}x. Click to edit.`
                  : `Loop: runs up to ${maxIterations} times. Click to edit.`
              } 
              placement="top"
            >
              <div
                onClick={isProgressView ? undefined : handleEdit}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  backgroundColor: selected || isHovered ? '#000000' : '#333333',
                  color: 'white',
                  padding: '4px 10px',
                  borderRadius: '16px',
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: isProgressView ? 'default' : 'pointer',
                  boxShadow: selected || isHovered ? '0 2px 8px rgba(0, 0, 0, 0.4)' : '0 1px 3px rgba(0,0,0,0.2)',
                  transition: 'all 0.2s ease',
                  userSelect: 'none',
                }}
              >
                <LoopIcon style={{ fontSize: '16px' }} />
                <span style={{ marginRight: '2px' }}>
                  {getChipLabel()}
                </span>
              </div>
            </Tooltip>

            {/* Control buttons - shown on hover/select, positioned below without affecting chip */}
            {showControls && (
              <div
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: '4px',
                  paddingTop: '4px',
                  justifyContent: 'center',
                }}
              >
                <Tooltip title="Edit loop iterations">
                  <IconButton
                    size="small"
                    onClick={handleEdit}
                    sx={{
                      backgroundColor: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      padding: '4px',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  >
                    <EditIcon sx={{ fontSize: '14px', color: '#333333' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete loop">
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleDelete}
                      disabled={saving}
                      sx={{
                        backgroundColor: 'white',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        padding: '4px',
                        '&:hover': {
                          backgroundColor: '#ffebee',
                        },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: '14px', color: '#e53935' }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            )}
          </div>
        </EdgeLabelRenderer>
      </>
    );
};

export default LoopLink;
